import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import api from "../lib/api";
import { ui, formatDate } from "../lib/ui";
import TrendingTerms from "../components/TrendingTerms";

function MostReported({ code }) {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/reports/topics/most-reported", { params: { section: code } }).then((r) => setData(r.data)).catch(() => {}); }, [code]);
  if (!data || (data.items.length === 0 && !(data.other?.count))) return null;
  return (
    <div className={ui.card + " p-5 mt-6"} data-testid="most-reported-panel">
      <h3 className="font-serif text-lg font-semibold text-[#8B1A1A] mb-3">Most reported topics</h3>
      <div className="space-y-2">
        {data.items.slice(0, 10).map((it) => (
          <div key={it.number} className="flex items-start gap-3 border border-[#E8DCC8] rounded-xl px-3 py-2">
            <span className="inline-flex items-center justify-center bg-[#8B1A1A] text-[#F5EAE0] font-semibold text-xs px-2 py-0.5 rounded-full mt-0.5">#{it.number}</span>
            <span className="flex-1 text-sm text-[#210201] line-clamp-2">{it.text || "(topic text pending)"}</span>
            <span className="text-xs text-[#671813] whitespace-nowrap">{it.count}× · {formatDate(it.latest)}</span>
          </div>
        ))}
        {data.other?.count > 0 && (
          <div className="flex items-center gap-3 border border-[#8B1A1A] rounded-xl px-3 py-2 bg-[#F3E5D8]">
            <span className="text-sm text-[#8B1A1A] font-medium flex-1">Other / unlisted topics</span>
            <span className="text-xs text-[#671813]">{data.other.count}× · {formatDate(data.other.latest)}</span>
          </div>
        )}
      </div>
    </div>
  );
}

export default function SectionTopics() {
  const { code } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    setLoading(true);
    api.get(`/reports/section/${code}`)
      .then((r) => setData(r.data))
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  }, [code]);

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-8 py-12">
      <Link to="/archive" className={ui.btnGhost + " mb-6 !px-0"}><ArrowLeft size={16} /> Back to archive</Link>
      {loading ? (
        <p className="text-[#5A4441]">Loading…</p>
      ) : error ? (
        <p className="text-[#8B1A1A]">Unknown section.</p>
      ) : (
        <>
          <h1 className="font-serif text-3xl sm:text-4xl font-bold text-[#8B1A1A] leading-tight">{data.label}</h1>
          <p className="mt-3 text-[#5A4441]">{data.count} reported {data.count === 1 ? "entry" : "entries"} · recurring themes from real candidates.</p>
          <div className="mt-6">
            <TrendingTerms section={code} title="Most recurring themes" />
          </div>
          <MostReported code={code} />
          <div className="mt-8 space-y-4">
            {data.items.length === 0 ? (
              <div className={ui.card + " p-10 text-center text-[#5A4441]"}>Nothing reported for this section yet.</div>
            ) : data.items.map((it) => (
              <div key={it.id} className={ui.card + " p-5"}>
                <div className="flex items-center justify-between gap-3 mb-2 flex-wrap">
                  <span className={ui.caption}>{formatDate(it.exam_date)} · {it.city}{it.country ? `, ${it.country}` : ""}</span>
                  {it.prediction_match && (
                    <span className={ui.pill}>Predicted: {it.prediction_match}</span>
                  )}
                </div>
                <p className="text-[#210201] leading-relaxed">{it.text}</p>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
